import React, {
  FC, KeyboardEvent, PropsWithChildren,
  ReactNode, RefObject, useCallback, useEffect, useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { BasePlacement, Instance, Placement } from '@popperjs/core';

import { VALUE_ENTER, VALUE_ESCAPE, VALUE_SPACE } from 'app/constants/keyValues';
import useOutsideClickHook from 'app/lib/hooks/useOutsideClickHook';

import PopperBody from './PopperBody';
import {
  ANIMATION_DURATION,
  createPopperInstance,
  FALLBACK_PLACEMENT,
} from './utils';

import styles from './Popper.module.scss';

export type BoundaryType = RefObject<HTMLElement> | string;

export type PopperProps = PropsWithChildren<{
    type: 'tooltip' | 'hint';
    content: ReactNode;
    // Чтобы поппер более точно менял свое расположение, ему необходимо задать boundary (границу), либо через ref, либо через id.
    // Пример использования boundary есть в сторибуке. Границей является элемент, за который поппер не должен выходить.
    boundary?: BoundaryType;
    placement?: Placement;
    fallbackPlacements?: Placement[];
    // Обертка нужна, чтобы к попперу был доступ с клавиатуры. Если вы оборачиваете так:
    // <Hint> <button>Кнопка</button> </Hint> или <Hint> <a href="">Ссылка</a> </Hint>
    // Тогда лучше childrenWrapperTag сделать div или span
    childrenWrapperTag?: 'div' | 'span' | 'button' | 'li';
    popperClassName?: string; // Класс для поппера
    childrenWrapperClassName?: string; // Класс для button, который оборачивает children
    onOpen?: () => void; // Срабатывает, когда поппер открывается
    onClose?: () => void; // Срабатывает, когда поппер закрывается
    'data-test'?: string;
  }>;

export const Popper: FC<PopperProps> = ({
  type,
  boundary,
  content,
  placement = 'right',
  fallbackPlacements = FALLBACK_PLACEMENT,
  childrenWrapperTag: Tag = 'button',
  popperClassName,
  childrenWrapperClassName,
  onOpen,
  onClose,
  children,
  ...props
}) => {
  const referenceElement = useRef(null);
  const popperElement = useRef<HTMLDivElement | null>(null);

  const [align, setAlign] = useState<'left' | 'center'>('left');
  const [isOpen, setIsOpen] = useState(false);
  const [showPortal, setIsShowPortal] = useState(false);
  const [popperInstance, setPopperInstance] = useState<Instance | null>(null);

  const [hideTimeoutId, setHideTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const isTooltip = type === 'tooltip';

  // --- Создание / удаление поппера

  const create = useCallback(() => {
    if (popperElement.current && referenceElement.current) {
      // Заметка: Установка аттрибута должна происходить раньше, чем вызов createPopperInstance
      // Чтобы анимация появления отработала плавно
      popperElement.current?.setAttribute('data-show', '');

      const instance = createPopperInstance(
          (referenceElement as RefObject<HTMLElement>).current as HTMLElement,
          popperElement.current,
          placement,
          fallbackPlacements,
          boundary,
          isTooltip,
          setAlign,
      );

      setPopperInstance(instance);
      setIsOpen(true);

      if (onOpen) {
        onOpen();
      }
    }
  }, [
    placement,
    boundary,
    popperElement.current,
    referenceElement.current,
    onOpen,
  ]);

  const destroy = useCallback(() => {
    popperElement.current?.removeAttribute('data-show');

    if (popperInstance) {
      popperInstance.destroy();
      setPopperInstance(null);
    }

    setIsShowPortal(false);
    setIsOpen(false);
  }, [popperElement.current, popperInstance]);

  // --- Показ / скрытие поппера

  const show = useCallback(() => {
    if (hideTimeoutId !== null) {
      clearTimeout(hideTimeoutId);
      setHideTimeoutId(null);
      destroy();
      setTimeout(() => setIsShowPortal(true), 0);
    }
    else {
      setIsShowPortal(true);
    }
  }, [hideTimeoutId]);

  const hide = useCallback(() => {
    const id = setTimeout(() => {
      destroy();
      setHideTimeoutId(null);
    }, ANIMATION_DURATION);

    setHideTimeoutId(id);
    setIsOpen(false);

    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    const keyCode = event.key;

    if (keyCode === VALUE_ESCAPE) {
      hide();
    }
    else if (!isTooltip && (keyCode === VALUE_SPACE || keyCode === VALUE_ENTER)) {
      isOpen ? hide() : show();
    }
  }, [isTooltip, isOpen]);

  // ---

  useEffect(() => {
    if (showPortal && !isOpen) {
      create();
    }
  }, [showPortal]);

  useEffect(() => {
    return (): void => destroy();
  }, []);

  useOutsideClickHook([popperElement, referenceElement], (event) => {
    if (isTooltip && isOpen) {
      event.stopPropagation();
      hide();
    }
  });

  // ---

  const referenceProps = isTooltip ? {
    onClick: isOpen ? hide : show,
  } : {
    onFocus: show,
    onBlur: hide,
    onMouseEnter: show,
    onMouseLeave: hide,
  };

  return (
    <>
      <Tag {...referenceProps}
           ref={referenceElement}
           data-test={classNames(props['data-test'], 'tooltip-wrapper')}
           type={Tag === 'button' ? 'button' : undefined}
           className={classNames(styles.childrenWrapper, childrenWrapperClassName)}
           onKeyDown={onKeyDown}>
        {children}
      </Tag>
      {showPortal && (
        <PopperBody isOpen={isOpen}
                    isTooltip={isTooltip}
                    align={align}
                    content={content}
                    popperElement={popperElement}
                    hide={hide}
                    popperClassName={popperClassName} />
      )}
    </>
  );
};

export const Tooltip: FC<Omit<PopperProps, 'type'>> = ({ children, ...props }) => (
  React.createElement(Popper, { ...props, type: 'tooltip' }, children)
);

type PopperHintProps = Omit<PopperProps, 'type' | 'placement'> & { placement?: BasePlacement };

export const Hint: FC<PopperHintProps> = ({ children, ...props }) => (
  React.createElement(Popper, { ...props, type: 'hint' }, children)
);


